import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { PACKAGES, ADDONS } from "../../data/content";

export default function QuoteTab({
  input, qPkg, setQPkg, qSize, setQSize, addPkgToQuote,
  qAddonKey, setQAddonKey, qAddonTier, setQAddonTier, qAddonObj, addFirmAddon,
  qAddonName, setQAddonName, qAddonPrice, setQAddonPrice, addAddonToQuote,
  quoteItems, removeQuoteItem, quoteTotal, chargePct, setChargePct, chargeNow,
}) {
  return (
    <div className="max-w-3xl">
      <h1 className="text-3xl font-extrabold tracking-tight mb-2">In-Person Quote</h1>
      <p className="text-sm text-zinc-400 mb-6">Build a live quote with a customer — pick packages and add any in-person add-ons at your quoted price.</p>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-4">
        <p className="text-sm font-semibold mb-3">Add a package</p>
        <div className="flex flex-wrap gap-3 items-end">
          <select value={qPkg} onChange={(e) => setQPkg(e.target.value)} className={`${input} flex-1 min-w-[180px]`} data-testid="quote-pkg">
            {PACKAGES.map((p) => (<option key={p.key} value={p.key}>{p.name}</option>))}
          </select>
          <select value={qSize} onChange={(e) => setQSize(e.target.value)} className={input} data-testid="quote-size">
            <option value="sedan">Sedan</option><option value="suv">SUV / Truck</option>
          </select>
          <button onClick={addPkgToQuote} className="bg-pink-600 hover:bg-pink-500 rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="quote-add-pkg"><Plus size={16} /> Add</button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-4">
        <p className="text-sm font-semibold mb-3">Add a firm-priced add-on (uses {qSize === "sedan" ? "Sedan" : "SUV/Truck"} pricing)</p>
        <div className="flex flex-wrap gap-3 items-end">
          <select value={qAddonKey} onChange={(e) => { setQAddonKey(e.target.value); setQAddonTier(0); }} className={`${input} flex-1 min-w-[180px]`} data-testid="quote-firm-addon">
            {ADDONS.map((a) => (<option key={a.key} value={a.key}>{a.name}</option>))}
          </select>
          {qAddonObj.tiers.length > 1 && (
            <select value={qAddonTier} onChange={(e) => setQAddonTier(Number(e.target.value))} className={input} data-testid="quote-firm-tier">
              {qAddonObj.tiers.map((t, ti) => (<option key={ti} value={ti}>{t.label} — ${qSize === "sedan" ? t.sedan : t.suv}</option>))}
            </select>
          )}
          <button onClick={addFirmAddon} className="bg-pink-600 hover:bg-pink-500 rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="quote-add-firm"><Plus size={16} /> Add</button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-[#121212] p-5 mb-4">
        <p className="text-sm font-semibold mb-3">Add an add-on (custom price)</p>
        <div className="flex flex-wrap gap-3 items-end">
          <input value={qAddonName} onChange={(e) => setQAddonName(e.target.value)} placeholder="e.g. Headlight restoration" className={`${input} flex-1 min-w-[180px]`} data-testid="quote-addon-name" />
          <input value={qAddonPrice} onChange={(e) => setQAddonPrice(e.target.value)} type="number" placeholder="$" className={`${input} w-24`} data-testid="quote-addon-price" />
          <button onClick={addAddonToQuote} className="bg-white/10 hover:bg-pink-600 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold flex items-center gap-1" data-testid="quote-add-addon"><Plus size={16} /> Add</button>
        </div>
      </div>

      <div className="rounded-2xl border border-pink-500/40 bg-[#121212] p-5">
        {quoteItems.length === 0 ? <p className="text-zinc-500 text-sm">No items yet.</p> : (
          <div className="space-y-2">
            {quoteItems.map((it, i) => (
              <div key={it.id} className="flex items-center justify-between border-b border-white/10 pb-2" data-testid={`quote-item-${i}`}>
                <span className="text-sm">{it.label}</span>
                <div className="flex items-center gap-3"><span className="font-semibold">${it.price.toFixed(2)}</span><button onClick={() => removeQuoteItem(i)} className="text-red-400"><Trash2 size={14} /></button></div>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex items-center justify-between text-lg"><span className="font-bold">Quote total</span><span className="font-extrabold text-pink-500" data-testid="quote-total">${quoteTotal.toFixed(2)}</span></div>
        <div className="mt-4 pt-4 border-t border-white/10">
          <p className="text-sm font-semibold mb-2">Charge a percentage in person</p>
          <p className="text-xs text-zinc-400 mb-3">If they paid a deposit online, charge the remaining %. E.g., 70% of the total.</p>
          <div className="flex items-center gap-3">
            <input value={chargePct} onChange={(e) => setChargePct(e.target.value.replace(/[^0-9]/g, ""))} type="number" min="0" max="100" className={`${input} w-24`} data-testid="quote-charge-pct" />
            <span className="text-sm text-zinc-400">% of ${quoteTotal.toFixed(2)}</span>
            <span className="ml-auto text-lg">Charge now: <span className="font-extrabold text-pink-500" data-testid="quote-charge-now">${chargeNow.toFixed(2)}</span></span>
          </div>
        </div>
      </div>
    </div>
  );
}
